import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Inbox, Mail, Phone, MapPin, Calendar, Image as ImageIcon,
  Loader2, X,
} from "lucide-react";

/**
 * Admin-only inbox for leads submitted via the public marketing site
 * contact form. Shows contact details, situation, and any photos the
 * homeowner uploaded.
 */
export default function AdminMarketingLeads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {photos, name, idx}

  useEffect(() => {
    if (!user?.is_admin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/marketing/leads");
        setLeads(data.leads || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load leads");
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (!user?.is_admin) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <p className="text-lg font-medium">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="admin-marketing-leads-page">
      <div className="flex items-start gap-4 mb-6">
        <div className="rounded-2xl bg-slate-900 text-white p-3">
          <Inbox size={22} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Public site inbox
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">
            Marketing Leads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Everyone who submitted the seller contact form on flippilot.us —
            newest first, with the photos they attached.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : leads.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--app-line)] p-12 text-center text-sm text-muted-foreground">
          No leads yet. Once homeowners fill out the contact form on your
          public site, they show up here.
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <LeadCard
              key={l.id}
              lead={l}
              onOpenPhoto={(idx) => setPreview({ photos: l.photos, name: l.name, idx })}
            />
          ))}
        </div>
      )}

      {preview && (
        <PhotoLightbox
          photos={preview.photos}
          name={preview.name}
          startIdx={preview.idx}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

function LeadCard({ lead, onOpenPhoto }) {
  const ts = lead.created_at ? new Date(lead.created_at).toLocaleString() : "—";
  return (
    <div
      className="rounded-2xl border border-[var(--app-line)] bg-white p-5"
      data-testid={`marketing-lead-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-lg font-display font-semibold">
            {lead.name || "Anonymous"}
          </div>
          <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
            {lead.email && (
              <a href={`mailto:${lead.email}`}
                 className="flex items-center gap-1 hover:text-foreground">
                <Mail size={11} /> {lead.email}
              </a>
            )}
            {lead.phone && (
              <a href={`tel:${lead.phone}`}
                 className="flex items-center gap-1 hover:text-foreground">
                <Phone size={11} /> {lead.phone}
              </a>
            )}
            {lead.address && (
              <span className="flex items-center gap-1">
                <MapPin size={11} /> {lead.address}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar size={11} /> {ts}
            </span>
          </div>
        </div>
        {lead.status && (
          <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] uppercase tracking-widest border border-blue-200">
            {lead.status}
          </span>
        )}
      </div>

      {(lead.timeline || lead.situation) && (
        <div className="flex flex-wrap gap-2 mb-2">
          {lead.timeline && (
            <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px] border border-amber-200">
              Timeline: {lead.timeline}
            </span>
          )}
          {lead.situation && (
            <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-800 text-[11px] border border-violet-200">
              {lead.situation}
            </span>
          )}
        </div>
      )}

      {lead.message && (
        <div className="text-sm text-foreground bg-slate-50 rounded-lg p-3 border border-slate-100 whitespace-pre-wrap">
          {lead.message}
        </div>
      )}

      {lead.photos?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
            <ImageIcon size={11} /> {lead.photos.length} photo{lead.photos.length > 1 ? "s" : ""}
          </div>
          <div className="flex flex-wrap gap-2">
            {lead.photos.map((p, i) => {
              const src = p.data_base64
                ? `data:image/jpeg;base64,${p.data_base64}`
                : (typeof p === "string" ? p : "");
              if (!src) return null;
              return (
                <button
                  key={i}
                  onClick={() => onOpenPhoto(i)}
                  className="w-20 h-20 rounded-lg overflow-hidden border border-[var(--app-line)] hover:ring-2 ring-slate-900 transition-all"
                  data-testid={`marketing-photo-${lead.id}-${i}`}
                >
                  <img src={src} alt={`photo-${i + 1}`} className="w-full h-full object-cover" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PhotoLightbox({ photos, name, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx || 0);
  const p = photos[idx];
  const src = p?.data_base64
    ? `data:image/jpeg;base64,${p.data_base64}`
    : (typeof p === "string" ? p : "");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 text-white/80 hover:text-white"
        aria-label="Close"
      >
        <X size={22} />
      </button>
      <div className="max-w-4xl max-h-[90vh] flex flex-col items-center gap-3"
           onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={name}
             className="max-w-full max-h-[80vh] rounded-xl object-contain" />
        <div className="text-white/70 text-xs flex items-center gap-3">
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="px-3 py-1 rounded-full border border-white/30 hover:bg-white/10 disabled:opacity-30"
          >
            Prev
          </button>
          <span>{idx + 1} / {photos.length}</span>
          <button
            onClick={() => setIdx((i) => Math.min(photos.length - 1, i + 1))}
            disabled={idx === photos.length - 1}
            className="px-3 py-1 rounded-full border border-white/30 hover:bg-white/10 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
